import { getCurrentToken } from './request'

// WebSocket 请求配置
interface WebSocketRequestConfig {
  url: string
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  params?: any
  data?: any
  timeout?: number
}

// WebSocket 响应
interface WebSocketResponse<T = any> {
  code: number
  data: T
  msg?: string
  message?: string
}

// 待处理的请求
interface PendingRequest {
  resolve: (value: any) => void
  reject: (error: Error) => void
  timeout: ReturnType<typeof setTimeout>
}

class WebSocketRequestManager {
  private ws: WebSocket | null = null
  private wsUrl: string = ''
  private pendingRequests: Map<string, PendingRequest> = new Map()
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private heartbeatTimer: ReturnType<typeof setTimeout> | null = null
  private isConnecting: boolean = false
  private requestIdCounter: number = 0
  private readonly reconnectInterval: number = 5000
  private readonly heartbeatInterval: number = 30000
  private readonly defaultTimeout: number = 10000

  // 获取 WebSocket URL
  private getWebSocketUrl(): string {
    const token = getCurrentToken()
    if (!token) {
      throw new Error('Token未设置，无法建立WebSocket连接')
    }

    // 从 HTTP URL 转换为 WebSocket URL
    const baseURL = 'https://api.ldsz.cc'
    const wsBaseURL = baseURL.replace(/^https?/, 'wss').replace(/^http/, 'ws')
    
    // WebSocket 路径格式：ws://host/ws/token/
    return `${wsBaseURL}/ws/${token}/`
  }

  // 连接 WebSocket
  private connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        resolve()
        return
      }

      if (this.isConnecting) {
        // 如果正在连接，等待连接完成
        const checkInterval = setInterval(() => {
          if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            clearInterval(checkInterval)
            resolve()
          } else if (!this.isConnecting) {
            clearInterval(checkInterval)
            reject(new Error('WebSocket连接失败'))
          }
        }, 100)
        return
      }

      this.isConnecting = true

      try {
        this.wsUrl = this.getWebSocketUrl()
        this.ws = new WebSocket(this.wsUrl)

        this.ws.onopen = () => {
          console.log('[WebSocket] 连接成功')
          this.isConnecting = false
          this.startHeartbeat()
          resolve()
        }

        this.ws.onmessage = (event) => {
          this.handleMessage(event)
        }

        this.ws.onerror = (error) => {
          console.error('[WebSocket] 连接错误:', error)
          this.isConnecting = false
          reject(new Error('WebSocket连接错误'))
        }

        this.ws.onclose = () => {
          console.log('[WebSocket] 连接关闭')
          this.isConnecting = false
          this.stopHeartbeat()
          this.handleReconnect()
        }
      } catch (error) {
        this.isConnecting = false
        reject(error)
      }
    })
  }

  // 处理接收到的消息
  private handleMessage(event: MessageEvent) {
    try {
      const message = JSON.parse(event.data)

      // 检查是否是心跳响应
      if (message.type === 'pong') {
        return
      }

      // 检查是否是 API 响应（包含 requestId）
      if (message.requestId) {
        const pendingRequest = this.pendingRequests.get(message.requestId)
        if (pendingRequest) {
          clearTimeout(pendingRequest.timeout)
          this.pendingRequests.delete(message.requestId)

          // 处理响应 - 支持多种响应格式
          if (message.code === 2000 || message.code === 200 || message.status === 'success') {
            // 如果响应中有 data 字段，返回 data，否则返回整个 message
            pendingRequest.resolve(message.data !== undefined ? message.data : message)
          } else {
            const errorMsg = message.msg || message.message || message.error || '请求失败'
            pendingRequest.reject(new Error(errorMsg))
          }
        }
        return
      }

      // 处理其他类型的消息（如推送消息）
      // 这里可以触发事件或调用回调
      // 注意：推送消息不应该包含 requestId，所以不会被当作 API 响应处理
      if (message.dump_data || message.contentType) {
        // 这是推送消息，不是 API 响应
        console.log('[WebSocket] 收到推送消息:', message)
        return
      }

      // 如果消息没有 requestId 但看起来像是响应，可能是后端直接返回的
      // 这种情况下，我们无法匹配到对应的请求，只能记录日志
      console.warn('[WebSocket] 收到未匹配的响应消息:', message)
    } catch (error) {
      console.error('[WebSocket] 解析消息失败:', error, event.data)
    }
  }

  // 发送请求
  async request<T = any>(config: WebSocketRequestConfig): Promise<T> {
    // 确保连接已建立
    await this.connect()

    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket未连接')
    }

    // 生成请求 ID
    const requestId = `req_${++this.requestIdCounter}_${Date.now()}`

    // 构建请求数据
    const requestData = {
      requestId,
      url: config.url,
      method: config.method || 'GET',
      params: config.params,
      data: config.data,
      timestamp: Date.now(),
    }

    // 创建 Promise
    return new Promise<T>((resolve, reject) => {
      // 设置超时
      const timeout = setTimeout(() => {
        this.pendingRequests.delete(requestId)
        reject(new Error(`请求超时: ${config.url}`))
      }, config.timeout || this.defaultTimeout)

      // 保存待处理的请求
      this.pendingRequests.set(requestId, {
        resolve,
        reject,
        timeout,
      })

      // 发送请求
      try {
        this.ws.send(JSON.stringify(requestData))
      } catch (error) {
        this.pendingRequests.delete(requestId)
        clearTimeout(timeout)
        reject(new Error(`发送请求失败: ${error}`))
      }
    })
  }

  // 心跳检测
  private startHeartbeat() {
    this.stopHeartbeat()
    this.heartbeatTimer = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        try {
          // 发送心跳消息
          this.ws.send(JSON.stringify({ type: 'ping', timestamp: Date.now() }))
        } catch (error) {
          console.error('[WebSocket] 发送心跳失败:', error)
          // 如果发送心跳失败，可能是连接已断开，尝试重连
          if (this.ws.readyState !== WebSocket.OPEN) {
            this.handleReconnect()
          }
        }
      } else {
        // 如果连接未打开，尝试重连
        this.handleReconnect()
      }
    }, this.heartbeatInterval)
  }

  private stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  // 重连处理
  private handleReconnect() {
    if (this.reconnectTimer) {
      return
    }

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      console.log('[WebSocket] 尝试重连...')
      this.connect().catch((error) => {
        console.error('[WebSocket] 重连失败:', error)
      })
    }, this.reconnectInterval)
  }

  // 关闭连接
  close() {
    this.stopHeartbeat()
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    if (this.ws) {
      this.ws.close()
      this.ws = null
    }
    // 清理所有待处理的请求
    this.pendingRequests.forEach((pendingRequest) => {
      clearTimeout(pendingRequest.timeout)
      pendingRequest.reject(new Error('WebSocket连接已关闭'))
    })
    this.pendingRequests.clear()
  }

  // 获取连接状态
  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN
  }
}

// 创建单例实例
const wsRequestManager = new WebSocketRequestManager()

// 导出 WebSocket 请求方法
export const wsRequest = {
  get: <T = any>(url: string, params?: any, timeout?: number): Promise<T> => {
    return wsRequestManager.request<T>({
      url,
      method: 'GET',
      params,
      timeout,
    })
  },

  post: <T = any>(url: string, data?: any, timeout?: number): Promise<T> => {
    return wsRequestManager.request<T>({
      url,
      method: 'POST',
      data,
      timeout,
    })
  },

  put: <T = any>(url: string, data?: any, timeout?: number): Promise<T> => {
    return wsRequestManager.request<T>({
      url,
      method: 'PUT',
      data,
      timeout,
    })
  },

  delete: <T = any>(url: string, params?: any, timeout?: number): Promise<T> => {
    return wsRequestManager.request<T>({
      url,
      method: 'DELETE',
      params,
      timeout,
    })
  },

  // 关闭连接
  close: () => {
    wsRequestManager.close()
  },

  // 检查连接状态
  isConnected: () => {
    return wsRequestManager.isConnected()
  },
}

export default wsRequest